import {
  type FollowUpAddEventInput,
  type FollowUpHistoryParams,
  type FollowUpSummaryParams,
  type FollowUpsListParams,
  api,
} from '@/lib/api-client'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

// ============================================================================
// Query Keys
// ============================================================================

export const followUpQueryKeys = {
  all: ['follow-ups'] as const,
  summary: (params?: FollowUpSummaryParams) =>
    [...followUpQueryKeys.all, 'summary', params] as const,
  list: (params?: FollowUpsListParams) => [...followUpQueryKeys.all, 'list', params] as const,
  assignees: (branchId?: number) => [...followUpQueryKeys.all, 'assignees', branchId] as const,
  history: (params: FollowUpHistoryParams) =>
    [...followUpQueryKeys.all, 'history', params] as const,
  detail: (id: number) => [...followUpQueryKeys.all, 'detail', id] as const,
  script: (id: number) => [...followUpQueryKeys.all, 'script', id] as const,
  byPatient: (patientId: number) => [...followUpQueryKeys.all, 'patient', patientId] as const,
}

// ============================================================================
// Queries
// ============================================================================

/** Queue tile counts + values for the "This week" tab. */
export function useFollowUpSummary(params?: FollowUpSummaryParams) {
  return useQuery({
    queryKey: followUpQueryKeys.summary(params),
    queryFn: () => api.followUps.summary(params),
    staleTime: 30 * 1000,
  })
}

/** Paginated/filtered follow-up queue rows. */
export function useFollowUps(params?: FollowUpsListParams) {
  return useQuery({
    queryKey: followUpQueryKeys.list(params),
    queryFn: () => api.followUps.list(params),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

/** Single follow-up (drawer detail: events, visits, packages). */
export function useFollowUp(id: number | null) {
  return useQuery({
    queryKey: followUpQueryKeys.detail(id ?? -1),
    queryFn: () => api.followUps.get(id!),
    enabled: id !== null && id > 0,
    staleTime: 15 * 1000,
  })
}

/** Suggested WA script for a follow-up (drawer textarea default). */
export function useFollowUpScript(id: number | null) {
  return useQuery({
    queryKey: followUpQueryKeys.script(id ?? -1),
    queryFn: () => api.followUps.script(id!),
    enabled: id !== null && id > 0,
    staleTime: 5 * 60 * 1000,
  })
}

/** CA list for a branch (assignee reassign select + history filter). */
export function useFollowUpAssignees(branchId?: number) {
  return useQuery({
    queryKey: followUpQueryKeys.assignees(branchId),
    queryFn: () => api.followUps.assignees(branchId),
    staleTime: 5 * 60 * 1000,
  })
}

/** History & report tab data for a given month. */
export function useFollowUpHistory(params: FollowUpHistoryParams) {
  return useQuery({
    queryKey: followUpQueryKeys.history(params),
    queryFn: () => api.followUps.history(params),
    staleTime: 60 * 1000,
  })
}

/** Follow-up episodes for a patient — used on the patient profile page. */
export function usePatientFollowUps(patientId: number | null) {
  return useQuery({
    queryKey: followUpQueryKeys.byPatient(patientId ?? -1),
    queryFn: () => api.followUps.byPatient(patientId!),
    enabled: patientId !== null && patientId > 0,
    staleTime: 30 * 1000,
  })
}

// ============================================================================
// Mutations
// ============================================================================

/** Reassign a follow-up to a different CA (or unassign with `null`). */
export function useAssignFollowUp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, assignedTo }: { id: number; assignedTo: number | null }) =>
      api.followUps.assign(id, assignedTo),

    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: followUpQueryKeys.all })
      queryClient.invalidateQueries({ queryKey: followUpQueryKeys.detail(variables.id) })
    },

    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reassign follow-up')
    },
  })
}

/** Record a manual outcome (No answer / Call back / Thinking / Not interested / Blacklisted / Note). */
export function useAddFollowUpEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: FollowUpAddEventInput }) =>
      api.followUps.addEvent(id, data),

    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: followUpQueryKeys.all })
      queryClient.invalidateQueries({ queryKey: followUpQueryKeys.detail(variables.id) })
      // Blacklisting flags the patient row too — refresh the profile if it's open.
      if (variables.data.type === 'blacklisted') {
        queryClient.invalidateQueries({ queryKey: ['patient', response.data.patientId] })
        queryClient.invalidateQueries({ queryKey: ['patients'] })
      }
    },

    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save outcome')
    },
  })
}

/** Manual "Refresh" button — reconcile + rebuild queues (admin/branch_manager only). */
export function useGenerateFollowUps() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data?: { branchId?: number }) => api.followUps.generate(data),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: followUpQueryKeys.all })
      toast.success('Follow-up queues refreshed')
    },

    onError: (error: Error) => {
      toast.error(error.message || 'Failed to refresh follow-up queues')
    },
  })
}
